import { useCallback, useOptimistic, useTransition } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { getFavoriteRestaurants } from "@/lib/client/api/restaurant"
import { useAuthStore } from "@/lib/store/auth-store"
import { Restaurant } from "@/lib/types"
import {
  addRestaurantFavoriteAction,
  removeRestaurantFavoriteAction,
} from "@/app/actions/restaurant-favorite"

/**
 * Custom hook to manage restaurant like/unlike functionality
 * @param restaurantId - ID of the restaurant to like/unlike
 * @returns Object containing like state and toggle function
 */
export function useRestaurantLike(restaurantId: number) {
  const queryClient = useQueryClient()
  const [isPending, startTransition] = useTransition()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  // Fetch favorite restaurants
  const { data, isLoading } = useQuery({
    queryKey: ["favorite-restaurants-mapping"],
    queryFn: async () => {
      const favoriteRestaurants = await getFavoriteRestaurants()
      const mapped: Record<number, boolean> = {}
      favoriteRestaurants.data.forEach((restaurant: Restaurant) => {
        mapped[restaurant.restaurant_id] = true
      })
      return mapped
    },
    staleTime: 1000 * 60 * 1000, // Keep data fresh for 1000 minutes
  })

  // Check if the current restaurant is liked
  const favoriteRestaurants = data || {}

  // Create optimistic state for immediate UI feedback
  const [optimisticFavorites, addOptimisticFavorite] = useOptimistic<
    Record<number, boolean>,
    { restaurantId: number; action: "add" | "remove" }
  >(favoriteRestaurants, (state, { restaurantId, action }) => {
    if (action === "add") {
      // Add the restaurant to favorites optimistically
      return {
        ...state,
        [restaurantId]: true,
      }
    } else {
      // Remove the restaurant from favorites optimistically
      return {
        ...state,
        [restaurantId]: false,
      }
    }
  })

  // Optimistic check if restaurant is liked
  const isLiked = optimisticFavorites[restaurantId]

  // Handle toggle favorite functionality
  const toggleFavorite = useCallback(
    (e?: React.MouseEvent) => {
      if (e) e.stopPropagation()

      // Don't proceed if not authenticated
      if (!isAuthenticated) {
        toast.error("Vui lòng đăng nhập để sử dụng tính năng này")
        return
      }

      // Don't allow clicking while a request is in progress
      if (isPending) return

      startTransition(async () => {
        try {
          // Set optimistic update first
          const isCurrentlyLiked = isLiked
          const action = isCurrentlyLiked ? "remove" : "add"

          // Apply optimistic update
          addOptimisticFavorite({ restaurantId, action })

          // Immediately update the cache to avoid flicker
          queryClient.setQueryData(
            ["favorite-restaurants-mapping"],
            (oldData: Record<number, boolean> = {}) => ({
              ...oldData,
              [restaurantId]: !isCurrentlyLiked,
            })
          )

          // Make API call based on current state
          const result = isCurrentlyLiked
            ? await removeRestaurantFavoriteAction(restaurantId)
            : await addRestaurantFavoriteAction(restaurantId)

          if (result.success) {
            // Silently refetch in the background without changing UI
            queryClient.invalidateQueries({
              queryKey: ["favorite-restaurants-mapping"],
              refetchType: "none", // Don't trigger an immediate refetch
            })
            queryClient.invalidateQueries({
              queryKey: ["favorite-restaurants-infinite"],
            })
          } else {
            // Show error toast
            toast.error(result.message || "Có lỗi xảy ra, vui lòng thử lại sau")

            // Revert the optimistic update
            queryClient.setQueryData(
              ["favorite-restaurants-mapping"],
              (oldData: Record<number, boolean> = {}) => ({
                ...oldData,
                [restaurantId]: isCurrentlyLiked,
              })
            )

            // Then refetch to ensure data consistency
            queryClient.invalidateQueries({
              queryKey: ["favorite-restaurants-mapping"],
              refetchType: "none",
            })

            queryClient.invalidateQueries({
              queryKey: ["favorite-restaurants-infinite"],
            })
          }
        } catch (error) {
          // Show error toast
          toast.error("Có lỗi xảy ra, vui lòng thử lại sau")
          console.error("Error toggling favorite:", error)

          // Refetch to reset to the correct state
          queryClient.invalidateQueries({
            queryKey: ["favorite-restaurants-mapping"],
          })
        }
      })
    },
    [
      isLiked,
      restaurantId,
      addOptimisticFavorite,
      isPending,
      queryClient,
      startTransition,
      isAuthenticated,
    ]
  )

  return {
    isLiked,
    isLoading,
    isPending,
    toggleFavorite,
    isAuthenticated,
  }
}
